import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Trash2, RotateCcw } from 'lucide-react';
import Layout from '../../components/Layout';
import departmentService from '../../services/departmentService';
import './DepartmentDeletePage.css';

const DepartmentDeletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [department, setDepartment] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    loadDepartment();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadDepartment = async () => {
    setLoading(true);
    setError(null);
    try {
      const deptData = await departmentService.getDepartmentById(parseInt(id || '0'));
      setDepartment(deptData);
      setLoading(false);
    } catch (err) {
      setError(t('department.failedToLoadDepartment'));
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!department) return;

    setIsDeleting(true);
    try {
      await departmentService.deleteDepartment(department.departmentId);
      navigate('/organization/departments', {
        state: { message: t('department.deletedSuccess') },
      });
    } catch (err) {
      setError(t('department.failedToLoadDepartment'));
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    navigate(`/organization/departments/${department?.departmentId}`);
  };

  if (loading) {
    return (
      <Layout>
        <div className="delete-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!department) {
    return (
      <Layout>
        <div className="delete-container">
          <div className="alert alert-danger">{t('department.failedToLoadDepartment')}</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="delete-container">
        <nav className="delete-breadcrumb">
          <button
            className="delete-breadcrumb-btn"
            onClick={handleCancel}
            title={t('common.back')}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('department.title')}
          </button>
        </nav>

        <div className="delete-content">
          <div className="delete-card delete-card-main">
            <div className="delete-card-header delete-card-header-warning">
              <Trash2 size={24} strokeWidth={2} className="delete-icon" />
              <h2 className="delete-card-title">{t('common.delete')}</h2>
            </div>

            {error && (
              <div className="alert alert-danger alert-dismissible fade show">
                {error}
              </div>
            )}

            <div className="delete-card-body">
              <div className="delete-warning-section">
                <p className="delete-warning-text">
                  {t('department.deleteConfirmationMessage')}
                </p>
              </div>

              <div className="delete-department-info">
                <div className="delete-info-item">
                  <label className="delete-info-label">{t('department.departmentName')}</label>
                  <p className="delete-info-value">{department.departmentName}</p>
                </div>

                <div className="delete-info-item">
                  <label className="delete-info-label">{t('department.code')}</label>
                  <p className="delete-info-value">{department.departmentCode}</p>
                </div>

                {department.description && (
                  <div className="delete-info-item delete-info-item-full">
                    <label className="delete-info-label">{t('common.description')}</label>
                    <p className="delete-info-value">{department.description}</p>
                  </div>
                )}
              </div>

              <div className="delete-actions">
                <button
                  className="btn-cancel"
                  onClick={handleCancel}
                  disabled={isDeleting}
                  title={t('common.cancel')}
                >
                  <RotateCcw size={16} strokeWidth={2} />
                  {t('common.cancel')}
                </button>
                <button
                  className="btn-delete"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  title={t('common.delete')}
                >
                  {isDeleting ? (
                    <>
                      <span className="btn-spinner"></span>
                      {t('common.delete')}
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} strokeWidth={2} />
                      {t('common.delete')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default DepartmentDeletePage;
